import React, {useCallback, useMemo} from 'react';

import {UserLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {getUserLabel} from 'src/utils/getUserLabel';

import {
    Cell,
    type CellOptions,
    Column, DataTable,
    HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';

import {FileNameCell} from './cells/file-name-cell';
import {FileSizeCell} from './cells/file-size-cell';
import {COLUMNS, COLUMNS_DATA, HEADERS} from './constants';
import {GeneratedUsageReportsPagination} from './pagination';

import type {GeneratedReport} from '../typedef';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ReportMetadata} from 'src/openapi/admin/models/ReportMetadata';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';


type Props = {
    rows: ReportMetadata[],
    totalCount: number,
    pagination: Pagination,
    onOpenReportDrawer: (report: GeneratedReport) => void,
};

export const GeneratedUsageReportsTable = ({rows, totalCount, pagination, onOpenReportDrawer}: Props) => {
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const openDrawer = useCallback((report: GeneratedReport) => {
        const files = rows
            .filter(({reportId}) => reportId === report.reportId)
            .sort((a, b) => a.fileOrder - b.fileOrder)
            .map(({fileId, fileName}) => ({id: fileId, name: fileName}));

        onOpenReportDrawer({...report, files});
    }, [rows, onOpenReportDrawer]);

    const renderedRows = useMemo(() => {
        const operations: Record<string, ReportMetadata[]> = {};
        rows.forEach(report => {
            if (operations[report.reportId]) {
                operations[report.reportId].push(report);
            } else {
                operations[report.reportId] = [report];
            }
        });

        return (Object
            .values(operations)
            .reduce((acc, curr, reportIndex) => [
                ...acc,
                ...[JSON.stringify({
                    reportId: curr[0].reportId,
                    reportIndex,
                    generatedAt: curr[0].generatedAt,
                    admin: curr[0].admin,
                }), ...curr.sort((a, b) => a.fileOrder - b.fileOrder)],
            ], [] as (string | ReportMetadata)[])
            .map(record => {
                if (typeof record === 'string') {
                    const data = JSON.parse(record);
                    return ({
                        id: JSON.stringify(data),
                        columnValues: {
                            fileName: data.generatedAt,
                            initiatorUser: data.admin,
                        },
                    });
                } else {
                    return ({
                        id: JSON.stringify({reportId: record, fileId: record.fileId}),
                        columnValues: {
                            fileName: record.fileName,
                            fileSize: record.fileSize,
                        },
                    });
                }
            })
        );
    }, [rows]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows?.length});

    return (
        <DataTable
            id="generated-usage-reports-table"
            data={{
                rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                    ...column,
                    width: getColumnWidthByColumnId(column.columnId),
                })),
            }}
            getHeaderTopOffset={() => 204}
            onStopResize={onStopResize}
            headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                switch (column.columnId) {
                    case COLUMNS.FILE_NAME:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <MainColumnHeader maxRowNumber={maxRowNumber}>
                                    <Group alignItems="center" gap="xs">
                                        <strong>{HEADERS[column.columnId]}</strong>
                                        <Tooltip
                                            color="light"
                                            anchor={({getAnchorProps, anchorRef}) => (
                                                <Icon ref={anchorRef} {...getAnchorProps()} type={Icons.INFO}/>
                                            )}>
                                            Reports are stored in the system for 3 days.
                                        </Tooltip>
                                    </Group>
                                </MainColumnHeader>
                            </Cell>
                        );
                    default:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </TableHeader>
                            </Cell>
                        );
                }
            }}
            renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                const {column: {columnId}} = rowsParams;
                const value = rowsParams.row.columnValues[columnId];
                const column = rowsParams.column;
                const rowId = rowsParams.row.id as string;
                const cellKey = `${rowId}-${columnId}`;

                const {fileId, reportId, reportIndex, generatedAt} = JSON.parse(rowId);

                const rowNumber = getPaginatedRowNumber({
                    ...pagination,
                    index: reportIndex + 1,
                });

                switch (columnId) {
                    case COLUMNS.FILE_NAME:
                        return (
                            <MainColumnCell
                                key={cellKey}
                                rowNumber={reportIndex !== undefined ? rowNumber : undefined}
                                maxRowNumber={maxRowNumber}
                                cellOptions={{...cellOptions, column}}>
                                <FileNameCell
                                    reportId={reportId}
                                    fileId={fileId}
                                    fileName={value as string}
                                    generatedAt={generatedAt}
                                    onOpenReport={openDrawer}
                                />
                            </MainColumnCell>
                        );
                    case COLUMNS.FILE_SIZE:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                align="right"
                                className="search-result-cell">
                                <FileSizeCell value={value as number}/>
                            </Cell>
                        );
                    case COLUMNS.INITIATOR:
                        const initiator = value as UserShortInfo | undefined;
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                {initiator ? (
                                    <UserLink
                                        userId={initiator.userId}
                                        caption={getUserLabel(initiator)}
                                        place="Generated Usage Reports"
                                    />
                                ) : <div/>}
                            </Cell>
                        );
                    default:
                        const displayValue = String(value);
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <EllipsisTextHint title={displayValue}>
                                    {displayValue}
                                </EllipsisTextHint>
                            </Cell>
                        );
                }
            }}
            paginationStripe={<GeneratedUsageReportsPagination totalCount={totalCount}/>}
        />
    );
};
